// ============================================================
// CCCo Intake Form — Loan & Field Configuration
// ============================================================

// Loan types
export const LOAN_TYPES = [
  { value: 'fix-flip',       label: 'Fix & Flip' },
  { value: 'ground-up',      label: 'Ground-Up Construction' },
  { value: 'bridge',         label: 'Bridge' },
  { value: 'stabilized',     label: 'Stabilized' },
  { value: 'dscr',           label: 'DSCR' },
  { value: 'mixed-use',      label: 'Mixed-Use' },
  { value: 'commercial',     label: 'Commercial' },
  { value: 'land',           label: 'Land' },
];

// Loan purposes (full list)
export const LOAN_PURPOSES = [
  'Purchase',
  'Refinance',
  'Cash-out refinance',
  'Construction',
  'Bridge',
];

// Which purposes apply per loan type (some loan types don't allow all purposes)
export const PURPOSES_BY_LOAN_TYPE = {
  'fix-flip':    ['Purchase', 'Refinance', 'Cash-out refinance', 'Construction', 'Bridge'],
  'ground-up':   ['Purchase', 'Refinance', 'Cash-out refinance', 'Construction', 'Bridge'],
  'bridge':      ['Purchase', 'Refinance', 'Cash-out refinance', 'Construction', 'Bridge'],
  'stabilized':  ['Purchase', 'Refinance', 'Cash-out refinance', 'Bridge'],
  'dscr':        ['Purchase', 'Refinance', 'Cash-out refinance'],
  'mixed-use':   ['Purchase', 'Refinance', 'Cash-out refinance', 'Bridge'],
  'commercial':  ['Purchase', 'Refinance', 'Cash-out refinance', 'Bridge'],
  'land':        ['Purchase', 'Refinance', 'Cash-out refinance', 'Construction', 'Bridge'],
};

// Property types
export const PROPERTY_TYPES = [
  'Single Family Residence',
  'Condo / Townhome',
  'Multi-Family (2-4 units)',
  'Multi-Family (5+ units)',
  'Mixed-Use',
  'Commercial',
  'Land / Vacant Lot',
  'Other',
];

export const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

export const ENTITY_TYPES = [
  'Individual',
  'LLC',
  'Corporation',
  'Partnership',
  'Trust',
  'Other',
];

export const EXIT_STRATEGIES = [
  'Sell',
  'Rehab & Sell',
  'Hold & Rent',
  'Refi Conv',
  'Refi DSCR',
  'Build & Sell',
  'Build & Hold',
  '1031 Exchange',
  'Other',
];

// UNIVERSAL Occupancy Status — applies to all property types
export const OCCUPANCY_STATUS = [
  'Vacant',
  'Partially Occupied',
  'Fully Occupied',
  'Owner-Occupied',
  'Tenant-Occupied',
];

export const STORIES = ['1', '2', '3', '4+'];

export const LOAN_TERMS = ['30 days', '3 months', '6 months', '12 months', '18 months', '24 months', '30 years', 'Other'];

export const PPP_OPTIONS = ['None', '1 year', '2 year', '3 year', '5 year', 'Flexible'];

// Source of funds — covers down payment, closing costs, AND reserves
// (renamed from DOWN_PMT_SOURCES — lenders care about all three buckets, not just down)
export const FUNDS_SOURCES = ['Own funds', 'Gift', 'Partner', 'Business', 'Other'];

export const FUNDS_STATUS = [
  'In account',
  'Pending from another source',
  'Coming from sale',
  'HELOC pending',
];

export const BANKRUPTCY_TYPES = ['Chapter 7', 'Chapter 11', 'Chapter 13'];

export const BANKRUPTCY_STATUS = ['Active', 'Discharged'];

// ============================================================
// PROPERTY DETAIL FIELDS — driven by Property Type
// ============================================================
export const PROPERTY_DETAIL_FIELDS = {
  bedrooms: {
    label: 'Bedrooms',
    types: ['Single Family Residence', 'Condo / Townhome'],
    type: 'number',
  },
  bathrooms: {
    label: 'Bathrooms',
    types: ['Single Family Residence', 'Condo / Townhome'],
    type: 'number',
  },
  sqft: {
    label: 'Sqft',
    types: ['Single Family Residence', 'Condo / Townhome', 'Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Commercial'],
    type: 'number',
  },
  lotSizeSqft: {
    label: 'Lot size (sqft)',
    types: ['Single Family Residence'],
    type: 'number',
  },
  stories: {
    label: 'Stories',
    types: ['Single Family Residence', 'Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Mixed-Use', 'Commercial'],
    type: 'select',
    options: STORIES,
  },
  floorLevel: {
    label: 'Floor level',
    types: ['Condo / Townhome'],
    type: 'number',
  },
  numberOfUnits: {
    label: '# of Units',
    types: ['Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Mixed-Use'],
    required: ['Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Mixed-Use'],
    type: 'number',
  },
  totalBeds: {
    label: 'Total beds',
    types: ['Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Mixed-Use'],
    type: 'number',
  },
  totalBaths: {
    label: 'Total baths',
    types: ['Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Mixed-Use'],
    type: 'number',
  },
  occupiedUnits: {
    label: 'Occupied units',
    types: ['Multi-Family (2-4 units)', 'Multi-Family (5+ units)', 'Mixed-Use'],
    type: 'number',
  },
  commercialSqft: {
    label: 'Commercial sqft',
    types: ['Mixed-Use'],
    type: 'number',
  },
  residentialSqft: {
    label: 'Residential sqft',
    types: ['Mixed-Use'],
    type: 'number',
  },
  tenantCount: {
    label: 'Tenant count',
    types: ['Commercial'],
    type: 'number',
  },
  landAcreage: {
    label: 'Acreage',
    types: ['Land / Vacant Lot'],
    required: ['Land / Vacant Lot'],
    type: 'number',
    placeholder: 'e.g. 2.5',
  },
  landZoning: {
    label: 'Zoning',
    types: ['Land / Vacant Lot'],
    type: 'select',
    options: ['Residential', 'Commercial', 'Industrial', 'Agricultural', 'Mixed', 'Unknown'],
  },
  landEntitled: {
    label: 'Entitled / Permitted?',
    types: ['Land / Vacant Lot'],
    type: 'select',
    options: ['Yes — fully entitled', 'Partially entitled', 'No — raw land', 'In process'],
  },
};

export function getVisiblePropertyFields(propertyType) {
  return Object.entries(PROPERTY_DETAIL_FIELDS).filter(([, cfg]) =>
    cfg.types?.includes(propertyType)
  );
}

export function isPropertyFieldRequired(fieldKey, propertyType) {
  const cfg = PROPERTY_DETAIL_FIELDS[fieldKey];
  return cfg?.required?.includes(propertyType) || false;
}

// ============================================================
// FIELD REQUIREMENTS ENGINE
// Returns which loan-structure fields show + which are required,
// stacking based on (loanType, loanPurpose, propertyType).
// ============================================================

// Loan types that need income/rent fields (income-producing programs)
const INCOME_LOAN_TYPES = ['stabilized', 'dscr', 'mixed-use', 'commercial'];

// Loan types that need NOI (Stabilized, Mixed-Use, Commercial — NOT DSCR which is simpler rent-only)
const NOI_LOAN_TYPES = ['stabilized', 'mixed-use', 'commercial'];

// Loan types where rehab/ARV are ALWAYS in scope
const ALWAYS_REHAB = ['fix-flip'];

// Loan types where rehab/ARV are OPTIONAL (controlled by toggle)
const OPTIONAL_REHAB = ['bridge', 'mixed-use', 'commercial'];

// Loan types where construction is the work being done
const CONSTRUCTION_LOAN_TYPES = ['ground-up'];

/**
 * Returns full visibility spec for the loan structure section.
 *
 * Output shape:
 * {
 *   show: { fieldKey: true/false },
 *   required: { fieldKey: true/false },
 *   labels: { fieldKey: customLabel } // optional overrides
 * }
 */
export function getLoanFieldSpec(loanType, loanPurpose, includesRehab) {
  const show = {};
  const required = {};
  const labels = {};

  if (!loanType || !loanPurpose) {
    return { show, required, labels };
  }

  // ====== LAYER 1: PURPOSE-DRIVEN BASE FIELDS ======

  // Purchase: needs purchase price + down payment
  if (loanPurpose === 'Purchase') {
    show.purchasePrice = true;
    required.purchasePrice = true;
    show.downPayment = true;
  }

  // Refinance: needs as-is value + existing loan balance (no down payment)
  if (loanPurpose === 'Refinance') {
    show.asIsValue = true;
    required.asIsValue = true;
    show.existingLoanBalance = true;
    required.existingLoanBalance = true;
  }

  // Cash-out refinance: as-is + existing balance + cash-out amount
  if (loanPurpose === 'Cash-out refinance') {
    show.asIsValue = true;
    required.asIsValue = true;
    show.existingLoanBalance = true;
    required.existingLoanBalance = true;
    show.cashOutAmount = true;
    required.cashOutAmount = true;
  }

  // Construction: needs current/land value + existing balance (if refinancing) or purchase price (if buying)
  // For Construction purpose, we always need a base value AND construction budget
  if (loanPurpose === 'Construction') {
    // If they're acquiring land/project AND constructing: purchase + down
    // If they're refinancing into construction: as-is + existing balance
    // Show BOTH — let borrower fill in what applies. (Lender will figure it out from data.)
    show.purchasePrice = true;
    labels.purchasePrice = 'Land / Property purchase price';
    show.downPayment = true;
    show.asIsValue = true;
    labels.asIsValue = 'Current land / project value';
    show.existingLoanBalance = true;
    show.constructionBudget = true;
    required.constructionBudget = true;
    show.arv = true;
    labels.arv = 'Completed value / ARV';
    required.arv = true;
  }

  // Bridge: needs purchase price OR as-is value (borrower picks based on scenario)
  if (loanPurpose === 'Bridge') {
    show.purchasePrice = true;
    labels.purchasePrice = 'Purchase price (if applicable)';
    show.downPayment = true;
    show.asIsValue = true;
    labels.asIsValue = 'Current value (if refi scenario)';
    show.existingLoanBalance = true;
  }

  // ====== LAYER 2: LOAN TYPE-DRIVEN OVERRIDES ======

  // Fix & Flip + Construction = "finish someone else's flip" scenario
  // Override the purchase fields and use Current Value + Remaining Rehab
  if (loanType === 'fix-flip' && loanPurpose === 'Construction') {
    delete show.purchasePrice;
    delete required.purchasePrice;
    delete show.downPayment;
    delete show.asIsValue;
    delete required.asIsValue;
    delete show.existingLoanBalance;
    delete required.existingLoanBalance;
    delete show.constructionBudget;
    delete required.constructionBudget;
    show.asIsValue = true;
    labels.asIsValue = 'Current value';
    required.asIsValue = true;
    show.rehabBudget = true;
    labels.rehabBudget = 'Remaining rehab budget';
    required.rehabBudget = true;
    show.arv = true;
    labels.arv = 'ARV';
    required.arv = true;
  }

  // Ground-Up + Construction = standard ground-up construction
  if (loanType === 'ground-up' && loanPurpose === 'Construction') {
    // Override generic Construction labels to be ground-up specific
    labels.purchasePrice = 'Land purchase price';
    labels.asIsValue = 'Current land value';
  }

  // Ground-Up + Refi/Cash-Out = refinancing during construction
  if (loanType === 'ground-up' && (loanPurpose === 'Refinance' || loanPurpose === 'Cash-out refinance')) {
    labels.asIsValue = 'Current land / project value';
    show.constructionBudget = true;
    labels.constructionBudget = 'Remaining construction budget';
    required.constructionBudget = true;
    show.arv = true;
    labels.arv = 'Completed value / ARV';
    required.arv = true;
  }

  // Ground-Up + Purchase = buying land + building
  if (loanType === 'ground-up' && loanPurpose === 'Purchase') {
    labels.purchasePrice = 'Land purchase price';
    show.constructionBudget = true;
    required.constructionBudget = true;
    show.arv = true;
    labels.arv = 'Completed value / ARV';
    required.arv = true;
  }

  // Ground-Up + Bridge
  if (loanType === 'ground-up' && loanPurpose === 'Bridge') {
    labels.asIsValue = 'Current project value';
    show.constructionBudget = true;
    labels.constructionBudget = 'Remaining construction budget';
    required.constructionBudget = true;
    show.arv = true;
    labels.arv = 'Completed value / ARV';
    required.arv = true;
  }

  // Fix & Flip — Rehab + ARV always required (except Construction subtype above)
  if (loanType === 'fix-flip' && loanPurpose !== 'Construction') {
    show.rehabBudget = true;
    required.rehabBudget = true;
    show.arv = true;
    required.arv = true;
  }

  // Bridge as loan type — rehab/ARV optional, controlled by toggle
  if (loanType === 'bridge' && includesRehab) {
    show.rehabBudget = true;
    show.arv = true;
  }

  // Mixed-Use / Commercial — rehab/ARV optional via toggle
  if ((loanType === 'mixed-use' || loanType === 'commercial') && includesRehab) {
    show.rehabBudget = true;
    show.arv = true;
  }

  // Bridge loan type + Bridge purpose = simplest scenario
  if (loanType === 'bridge' && loanPurpose === 'Bridge') {
    // Override: simplest form, just current value, existing balance, loan amount
    delete show.purchasePrice;
    delete show.downPayment;
    labels.asIsValue = 'Current value';
    required.asIsValue = true;
  }

  // Land loan type + Construction = building on land
  if (loanType === 'land' && loanPurpose === 'Construction') {
    show.constructionBudget = true;
    required.constructionBudget = true;
    show.arv = true;
    labels.arv = 'Completed value / ARV';
    required.arv = true;
  }

  // ====== LAYER 3: INCOME-PRODUCING LOAN TYPES ======
  // Income fields show for DSCR, Stabilized, Mixed-Use, Commercial — regardless of purpose

  if (INCOME_LOAN_TYPES.includes(loanType)) {
    show.currentMonthlyRent = true;
    show.projectedMonthlyRent = true;
    // At least one is required — handled in validation
  }

  if (NOI_LOAN_TYPES.includes(loanType)) {
    show.annualNOI = true;
  }

  // Vacancy rate & OpEx ratio — secondary info for income-producing
  if (INCOME_LOAN_TYPES.includes(loanType)) {
    show.vacancyRate = true;
    show.operatingExpenseRatio = true;
  }

  // ====== LOAN AMOUNT — ALWAYS REQUIRED ======
  show.loanAmount = true;
  required.loanAmount = true;

  return { show, required, labels };
}

// Whether to show the "Includes rehab/improvements?" toggle for this loan type
export function showsRehabToggle(loanType) {
  return OPTIONAL_REHAB.includes(loanType);
}

// Whether this loan type qualifies for income/rent fields
export function isIncomeProducingLoan(loanType) {
  return INCOME_LOAN_TYPES.includes(loanType);
}

// Whether this loan type uses NOI
export function usesNOI(loanType) {
  return NOI_LOAN_TYPES.includes(loanType);
}

// Get available loan purposes for a given loan type
export function getAvailablePurposes(loanType) {
  if (!loanType) return LOAN_PURPOSES;
  return PURPOSES_BY_LOAN_TYPE[loanType] || LOAN_PURPOSES;
}
